
import { Picture } from '@core/picture/picture.types';
import { FlatShippingRate } from '@core/shipping/shipping-rate-flat.types';
import { Deserializable } from '../utils/deserializable.types';

/**
 * Enum representing the different types of products.
 */
export enum ProductType {
    /**
     * Represents a physical product.
     */
    Product,
    /**
     * Represents a service.
     */
    Service,
    /**
     * Represents a digital product.
     */
    Digital
}

/**
 * Represents the count of different types of products.
 */
export interface ProductCount {
    /**
     * The number of variant items.
     */
    nbVariantItems: number;
    /**
     * The number of inventory items.
     */
    nbInventoryItems: number;
    /**
     * The number of products.
     */
    nbProducts: number;
}

/**
 * Represents a dictionary record with text, update date and updated by information.
 */
export interface DictionaryRecord {
    /** The text value of the dictionary record. */
    text: string;
    /** The date when the dictionary record was last updated. */
    updatedAt: Date;
    /** The name of the user who last updated the dictionary record. */
    updatedBy: string;
}

/**
 * A dictionary object that contains language keys and their corresponding dictionary records.
 */
export interface Dictionary {
    [key: string]: { // language
        [key: string]: DictionaryRecord; // dictionary records
    };
}

/**
 * Represents the lead time for a product.
 */
export interface LeadTime {
    /**
     * The type of lead time.
     */
    type: 'none' | 'relative';
    /**
     * The duration of the lead time, in days.
     */
    duration: number;
}

/**
 * Represents the availability of a product.
 */
export interface Availability {
    /**
     * Indicates whether the availability of the product requires approval.
     */
    requiresApproval: boolean;
    /**
     * Indicates the type of availability.
     * 
     * Possible values are:
     * - `'always'`: The product is always available.
     * - `'specific'`: The product is available on specific dates.
     * - `'repeat'`: The product is available on a repeating schedule.
     */
    type: 'always' | 'specific' | 'repeat';
    /**
     * An array of specific dates when the product is available.
     * 
     * This property is only used when `type` is set to `'specific'`.
     */
    times?: Date[];
    /**
     * The period of the repeating schedule.
     * 
     * This property is only used when `type` is set to `'repeat'`.
     * 
     * Possible values are:
     * - `'daily'`: The product is available every day.
     * - `'weekly'`: The product is available every week.
     * - `'monthly'`: The product is available every month.
     * - `'yearly'`: The product is available every year.
     */
    period?: 'daily' | 'weekly' | 'monthly' | 'yearly';
    /**
     * An array of repeating schedule items.
     * 
     * This property is only used when `type` is set to `'repeat'`.
     */
    repeat?: any[];
}

/**
 * Refund policy for a product.
 */
export interface RefundPolicy {
    /**
     * The policy description.
     */
    policy: string;
    /**
     * The number of hours till which the customer gets refunded free of charge.
     */
    deadline: number;
    /**
     * The percentage that gets refunded after the deadline.
     */
    percentage: number;
}


/**
 * Represents a variant item of a product.
 */
export interface VariantItem {
    /** The unique identifier of the variant item. */
    _id: string;
    /** The unique identifier of the SKU. */
    skuId: string;
    /** The selected attributes of the variant item. */
    attributes: SelectedAttribute[];
    /** The custom field values of the variant item. */
    customFieldValues?: any[]; // Used by the product order to store the selected value
    /** Indicates whether the variant item is valid or not. */
    valid: boolean;
    /** The price of the variant item. */
    price: number;
    /** The discounted price of the variant item. */
    discountPrice?: number;
    /** The discount message of the variant item. */
    discountMessage?: string;
    /** Indicates whether the variant item has unlimited quantity or not. */
    unlimitedQuantity?: boolean;
    /** The quantity of the variant item. */
    quantity: number;
    /** The duration of the variant item. */
    duration: number;
    /** The photos of the variant item. */
    photos: Picture[];
    /** The dimensions of the variant item. */
    dimensions: Dimension;
    // Optional for Google
    /** Indicates whether the variant item is for adults or not. */
    adult?: boolean;
    /** The brand of the variant item. */
    brand?: string;
    /** The color of the variant item. */
    color?: string;
    /** The product reference (defined internally by the merchant). */
    reference?: string;
    /** The GTIN (Global Trade Item Number) of the variant item. */
    gtin?: string;
    /** The MPN (Manufacturer Part Number) of the variant item. */
    mpn?: string;
    /** The material of the variant item. */
    material?: string;
    /** The pattern of the variant item. */
    pattern?: string;
    /** The height of the variant item. */
    productHeight?: number; // In cm
    /** The length of the variant item. */
    productLength?: number; // In cm
    /** The width of the variant item. */
    productWidth?: number; // In cm
    /** The weight of the variant item. */
    productWeight?: number; // In kg
    /** The size of the variant item. */
    size?: string;
    /** The age group of the variant item. */
    ageGroup?: string;
    /** The condition of the variant item. */
    condition?: string;
    /** The gender of the variant item. */
    gender?: string;
    /** The energy efficiency class of the variant item. */
    energyEfficiencyClass?: string;
    /** The minimum energy efficiency class of the variant item. */
    minEnergyEfficiencyClass?: string;
    /** The maximum energy efficiency class of the variant item. */
    maxEnergyEfficiencyClass?: string;
    /** The product highlights of the variant item. */
    productHighlights?: string[];
    // Optional for Google (which ones are displayed on the merchant website)
    /** Indicates whether the adult information is displayed or not. */
    adultDisplayed?: boolean;
    /** Indicates whether the brand information is displayed or not. */
    brandDisplayed?: boolean;
    /** Indicates whether the color information is displayed or not. */
    colorDisplayed?: boolean;
    /** Indicates whether the reference information is displayed or not. */
    referenceDisplayed?: string;
    /** Indicates whether the GTIN information is displayed or not. */
    gtinDisplayed?: boolean;
    /** Indicates whether the MPN information is displayed or not. */
    mpnDisplayed?: boolean;
    /** Indicates whether the material information is displayed or not. */
    materialDisplayed?: boolean;
    /** Indicates whether the pattern information is displayed or not. */
    patternDisplayed?: boolean;
    /** Indicates whether the product height information is displayed or not. */
    productHeightDisplayed?: boolean;
    /** Indicates whether the product length information is displayed or not. */
    productLengthDisplayed?: boolean;
    /** Indicates whether the product width information is displayed or not. */
    productWidthDisplayed?: boolean;
    /** Indicates whether the product weight information is displayed or not. */
    productWeightDisplayed?: boolean;
    /** Indicates whether the size information is displayed or not. */
    sizeDisplayed?: boolean;
    /** Indicates whether the age group information is displayed or not. */
    ageGroupDisplayed?: boolean;
    /** Indicates whether the condition information is displayed or not. */
    conditionDisplayed?: boolean;
    /** Indicates whether the gender information is displayed or not. */
    genderDisplayed?: boolean;
    /** Indicates whether the energy efficiency class information is displayed or not. */
    energyEfficiencyClassDisplayed?: boolean;
    /** Indicates whether the minimum energy efficiency class information is displayed or not. */
    minEnergyEfficiencyClassDisplayed?: boolean;
    /** Indicates whether the maximum energy efficiency class information is displayed or not. */
    maxEnergyEfficiencyClassDisplayed?: boolean;
    /** Indicates whether the product highlights information is displayed or not. */
    productHighlightsDisplayed?: boolean;
    // End of Google optional information
}


/**
 * Represents an attribute of a product.
 */
export interface Attribute {
    /** The type of the attribute. */
    type: string;
    /** The name of the attribute. */
    name: string;
    /** The description of the attribute. */
    description: string;
    /** The options available for the attribute. */
    options: string[];
    /** Whether the attribute should be displayed as a filter. */
    displayedAsFilter: boolean;
}

/**
 * Represents a selected attribute of a product.
 */
export interface SelectedAttribute {
    /**
     * The name of the attribute.
     */
    name: string;
    /**
     * The selected option of the attribute.
     */
    option: string;
}

/**
 * Represents the dimensions of a product.
 */
export interface Dimension {
    /** The height of the product in millimeters. */
    height: number;
    /** The width of the product in millimeters. */
    width: number;
    /** The length of the product in millimeters. */
    length: number;
    /** The weight of the product in grams. */
    weight: number;
}

/**
 * Represents an upselling option for a product.
 */
export interface UpsellingOption {
    /** The unique identifier of the upselling option. */
    _id: string;
    /** The parent SKU identifier of the upselling option. */
    parentSkuId: string;
    /** The name of the upselling option. */
    name: string;
    /** The price of the upselling option. */
    price: number;
    /** Indicates whether the upselling option has unlimited quantity. */
    unlimitedQuantity?: boolean;
    /** Indicates whether multiple purchases of the upselling option are allowed. */
    multiplePurchaseAllowed: boolean;
    /** The quantity of the upselling option. */
    quantity: number;
    /** The duration of the upselling option. */
    duration: number;
    /** The dimensions of the upselling option. */
    dimensions: Dimension;
}

/**
 * Represents an item that can be upsold to a customer.
 */
export interface UpsellingItem {
    /** The name of the upselling item. */
    name: string;
    /** The available options for the upselling item. */
    options: UpsellingOption[];
    /** The maximum number of times the upselling item can be purchased. */
    maxPurchases: number;
}

/**
 * Represents a custom description for a product.
 */
export interface CustomDescription {
    /**
     * The name of the custom description.
     */
    name: string;
    /**
     * The description of the custom description.
     */
    description: string;
    /**
     * The icon of the custom description.
     */
    icon: string;
}

/**
 * Enum representing the types of validity reports.
 */
export enum ValidityReportType {
    /**
     * Represents an error report.
     */
    ERROR = 'error',
    /**
     * Represents a warning report.
     */
    WARNING = 'warning',
    /**
     * Represents an information report.
     */
    INFO = 'info'
}

/**
 * Enum representing the sources of validity reports.
 */
export enum ValidityReportSource {
    /**
     * Represents a report from QART.
     */
    QART = 'qart',
    /**
     * Represents a report from Google.
     */
    GOOGLE = 'google',
    /**
     * Represents a report from Instagram.
     */
    INSTAGRAM = 'instagram'
}

/**
 * Represents a validity report for a product.
 */
export interface ValidityReport {
    /**
     * The type of the validity report.
     */
    type: ValidityReportType;
    /**
     * The source of the validity report.
     */
    source: ValidityReportSource;
    /**
     * The code associated with the validity report.
     */
    code: string;
    /**
     * The property associated with the validity report, if any.
     */
    property?: string;
    /**
     * The value associated with the validity report, if any.
     */
    value?: string;
}

/**
 * Represents a custom field that can be added to a product.
 */
export interface CustomField {
    /**
     * The type of the custom field.
     * Can be "input", "radio", "select", "checkbox", "datepicker", etc.
     */
    type: string;
    /**
     * The label of the custom field.
     */
    label: string;
    /**
     * The description of the custom field.
     */
    description: string;
    /**
     * Indicates whether the custom field is required or not.
     */
    required: boolean;
    /**
     * The extra cost of the custom field in case it is used.
     */
    price: number;
    /**
     * The constraints of the custom field.
     */
    extras: {
        /**
         * Indicates whether the custom field is multiline or not.
         * Only applicable for text fields.
         */
        multiline?: boolean;
        /**
         * Indicates whether the custom field can have multiple values selected or not.
         * Only applicable for select and checkbox fields.
         */
        multiple?: boolean;
        /**
         * The minimum value of the custom field.
         * Only applicable for numeric fields.
         */
        minValue?: number;
        /**
         * The maximum value of the custom field.
         * Only applicable for numeric fields.
         */
        maxValue?: number;
        /**
         * The minimum length of the custom field.
         * Only applicable for string and array fields.
         */
        minLength?: number;
        /**
         * The maximum length of the custom field.
         * Only applicable for string and array fields.
         */
        maxLength?: number;
        /**
         * The regex pattern of the custom field.
         * Only applicable for string fields.
         */
        pattern?: string;
        /**
         * The accepted values of the custom field.
         * Only applicable for select and checkbox fields.
         */
        acceptedValues?: {
            /**
             * The value of the accepted value.
             */
            value: any;
            /**
             * The extra cost of the accepted value in case it is selected.
             */
            price: number;
        }[];
        /**
         * The type of the accepted values.
         * Can be "text", "number", "email", etc.
         * Only applicable for select and checkbox fields.
         */
        acceptedValueType?: string;
    };
}

/**
 * Represents a product in the merchant website.
 */
export class Product implements Deserializable {
    /**
     * The unique identifier of the product.
     */
    public _id: string;

    /**
     * The identifier of the product.
     */
    public productId: string;

    /**
     * The identifier of the category that the product belongs to.
     */
    public categoryId: string;

    /**
     * The identifier of the merchant that owns the product.
     */
    public merchantId?: string;

    /**
     * Indicates whether the product is displayed online or not.
     */
    public displayOnline: boolean;

    /**
     * The position of the product in the display order.
     */
    public displayPosition: number;

    /**
     * The language in which the product has been loaded.
     */
    public currentLanguage?: string;

    /**
     * The name of the product.
     */
    public name: string;

    /**
     * The type of the product.
     */
    public type: ProductType;

    /**
     * The tags associated with the product.
     */
    public tags: string[];

    /**
     * The description of the product.
     */
    public description: string;

    /**
     * The comment associated with the product.
     */
    public comment: string;

    /**
     * The photos of the product.
     */
    public photos: Picture[];

    /**
     * The SEO information of the product.
     */
    // eslint-disable-next-line @typescript-eslint/naming-convention
    public SEO: {
        /**
         * The title of the page.
         */
        pageTitle: string;

        /**
         * The description of the page.
         */
        description: {
            /**
             * The language of the description.
             */
            language: string;

            /**
             * The input of the description.
             */
            input: string;

            /**
             * The text of the description.
             */
            text: string;

            /**
             * The date when the description was last updated.
             */
            updatedAt: Date;

            /**
             * The source of the description.
             */
            source: string;

            /**
             * Indicates whether the description is approved by the user or not.
             */
            userApproved: boolean;

            /**
             * The user who approved the description.
             */
            approvedBy: string;

            /**
             * The date when the description was approved.
             */
            approvedAt: Date;
        };

        /**
         * The URI of the page.
         */
        uri: string;
    };

    /**
     * The refund policy of the product.
     */
    public refund: RefundPolicy;

    /**
     * Indicates whether the product is shippable or not.
     */
    public shippable: boolean;

    /**
     * The lead time of the product.
     */
    public leadTime: LeadTime;

    /**
     * The availability of the product.
     */
    public availability: Availability;

    /**
     * Indicates whether the product has variants or not.
     */
    public hasVariants: boolean;

    /**
     * The attributes of the product.
     */
    public attributes: Attribute[];

    /**
     * The variant items of the product.
     */
    public variantItems: VariantItem[];

    /**
     * The upselling items of the product.
     */
    public upsellingItems: UpsellingItem[];

    /**
     * The Google products associated with the product.
     */
    public googleProducts: any[];

    /**
     * The validity report of the product.
     */
    public validity: ValidityReport[];

    /**
     * Indicates whether the product is featured or not.
     */
    public featured: boolean;

    /**
     * Indicates whether the product is fake or not.
     */
    public fake: boolean;

    /**
     * Indicates whether some fields of the product are AI generated.
     */
    public aiGenerated?: {
        /**
         * Indicates whether the title of the product is AI generated.
         */
        title?: boolean;
        /**
         * Indicates whether the description of the product is AI generated.
         */
        description?: boolean;
    };
    
    /**
     * The flat shipping rates of the product.
     */
    public shippingFlatRates?: FlatShippingRate[];

    /**
     * The custom descriptions of the product.
     */
    public customDescriptions?: CustomDescription[];

    /**
     * The custom fields of the product.
     */
    public customFields?: CustomField[];

    /**
     * The dictionary of the product.
     */
    public dictionary?: Dictionary;

    /**
     * The date when the product was created.
     */
    public createdAt: Date;

    /**
     * The user who created the product.
     */
    public createdBy: string;

    /**
     * The date when the product was last updated.
     */
    public updatedAt?: Date;

    /**
     * The user who last updated the product.
     */
    public updatedBy?: string;

    /**
     * Deserializes the input object into the current instance.
     * @param input The input object to deserialize.
     * @returns The deserialized instance.
     */
    deserialize(input: any): this {
        Object.assign(this, input);
        return this;
    }

    /**
     * Indicates whether the product has multiple prices or not.
     * @returns True if the product has multiple prices, false otherwise.
     */
    public hasMultiplePrices(): boolean {
        return this.getMaximumPrice() !== this.getMinimumPrice();
    }

    /**
     * Indicates whether the product has a discount or not.
     * @returns True if the product has a discount, false otherwise.
     */
    public hasDiscount(): boolean {
        return this.variantItems
            .filter(variantItem => variantItem.valid)
            .some(variantItem => variantItem.discountPrice ? variantItem.discountPrice !== variantItem.price : false);
    }

    /**
     * Indicates whether the product has a discount message or not.
     * @returns True if the product has a discount message, false otherwise.
     */
    public hasDiscountMessage(): boolean {
        return this.variantItems
            .filter(variantItem => variantItem.valid)
            .some(variantItem => variantItem.discountMessage ? variantItem.discountMessage !== '' : false);
    }

    /**
     * Indicates whether the product has out of stock items or not.
     * @returns True if the product has out of stock items, false otherwise.
     */
    public hasOutOfStock(): boolean {
        return this.variantItems
            .filter(variantItem => variantItem.valid)
            .some(variantItem => !variantItem.unlimitedQuantity && variantItem.quantity === 0);
    }

    /**
     * Indicates whether the product is out of stock or not.
     * @returns True if the product is out of stock, false otherwise.
     */
    public isOutOfStock(): boolean {
        return this.type === ProductType.Product && this.variantItems
            .filter(variantItem => variantItem.valid)
            .every(variantItem => !variantItem.unlimitedQuantity && variantItem.quantity === 0);
    }

    /**
     * Indicates whether the product has stock or not.
     * @returns True if the product has stock, false otherwise.
     */
    public hasStock(): boolean {
        return this.variantItems
            .filter(variantItem => variantItem.valid)
            .some(variantItem => !variantItem.unlimitedQuantity);
    }

    /**
     * Gets the maximum original price of the product.
     * @returns The maximum original price of the product.
     */
    public getMaximumOriginalPrice(): number {
        return Math.max(...this.variantItems.filter(variantItem => variantItem.valid).map(variantItem => variantItem.price as number));
    }

    /**
     * Gets the minimum original price of the product.
     * @returns The minimum original price of the product.
     */
    public getMinimumOriginalPrice(): number {
        return Math.min(...this.variantItems.filter(variantItem => variantItem.valid).map(variantItem => variantItem.price as number));
    }

    /**
     * Gets the maximum price of the product.
     * @returns The maximum price of the product.
     */
    public getMaximumPrice(): number {
        const validVariantItems: VariantItem[] = this.variantItems.filter(variantItem => variantItem.valid);
        if (validVariantItems.length > 0) {
            return Math.max(...validVariantItems.map(elem => elem.discountPrice ? elem.discountPrice as number : elem.price as number));
        } else {
            return 0;
        }
    }

    /**
     * Gets the minimum price of the product.
     * @returns The minimum price of the product.
     */
    public getMinimumPrice(): number {
        const validVariantItems: VariantItem[] = this.variantItems.filter(variantItem => variantItem.valid);
        if (validVariantItems.length > 0) {
            return Math.min(...validVariantItems.map(elem => elem.discountPrice ? elem.discountPrice as number : elem.price as number));
        } else {
            return 0;
        }
    }

    /**
     * Indicates whether the product has multiple durations or not.
     * @returns True if the product has multiple durations, false otherwise.
     */
    public hasMultipleDurations(): boolean {
        return this.getMaximumDuration() !== this.getMinimumDuration();
    }

    /**
     * Gets the maximum duration of the product.
     * @returns The maximum duration of the product.
     */
    public getMaximumDuration(): number {
        return Math.max(...this.variantItems.filter(variantItem => variantItem.valid).map(variantItem => variantItem.duration as number));
    }

    /**
     * Gets the minimum duration of the product.
     * @returns The minimum duration of the product.
     */
    public getMinimumDuration(): number {
        return Math.min(...this.variantItems.filter(variantItem => variantItem.valid).map(variantItem => variantItem.duration as number));
    }

    /**
     * Indicates whether the product is available or not.
     * @returns True if the product is available, false otherwise.
     */
    public isAvailable(): boolean {
        if (this.type === ProductType.Product) {
            return this.variantItems.filter(variantItem => variantItem.valid).some(variantItem => variantItem.unlimitedQuantity || (!variantItem.unlimitedQuantity && variantItem.quantity > 0));
        } else {
            return true;
        }
    }
}
